import Vue from 'vue'
import Vuex from 'vuex'
import * as Api from '@/api/CheckoutApi'
import * as Analytics from '@/api/AnalyticsApi'
import {
   allValidationMessages,
   informationValidationMessages,
   orderTypeValidationMessages,
   shippingValidationMessages,
   paymentValidationMessages,
   ValidationMessage,
} from './Validation'
import { CheckoutPageModel } from '@ViewModels/CheckoutPageModel'
import { CartTotalsModel } from '@ViewModels/CartTotalsModel'
import { StoreCheckoutDto } from '@ViewModels/StoreCheckoutDto'
import { CheckoutStreetAddressDto } from '@ViewModels/CheckoutStreetAddressDto'
import { CheckoutVerificationResult } from '@ViewModels/CheckoutVerificationResult'
import { SchoolSearchResultModel } from '@ViewModels/SchoolSearchResultModel'
import { CheckoutResponse } from '@ViewModels/CheckoutResponse'

Vue.use(Vuex)

export type Step = 'information' | 'order_type' | 'shipping' | 'payment'

export interface CheckoutState {
   modelData: CheckoutPageModel
   modelDataLoaded: boolean
   purchase: StoreCheckoutDto
   schoolLookup: {
      schoolId: string
      schoolName: string
      schoolAddress: string
   }
   step: Step
   viewedSteps: Step[]
   processingCheckout: boolean
   showErrors: boolean
   verificationErrorMessage: string
   verificationErrors: string[]
   accountType: 'unknown' | 'existing' | 'new'
   recaptchaRefreshRequired: boolean
   requireBillingAddress: boolean
}

export default new Vuex.Store<CheckoutState>({
   state: {
      modelDataLoaded: false,
      step: 'information',
      viewedSteps: [],
      processingCheckout: false,
      showErrors: false,
      verificationErrorMessage: '',
      verificationErrors: [],
      recaptchaRefreshRequired: false,
      accountType: 'unknown',
      requireBillingAddress: false,
      modelData: {
         Id: '',
         CartItems: [],
         Totals: {} as CartTotalsModel,
         PromoCodes: [],
         ShippingCountryId: '',
         AllowClickAndCollect: false,
         ClickAndCollect: false,
         CurrencyCode: '',
         CurrencySymbol: '$',
         CountryCode: 'NZ',
         MarketId: '',
         MarketName: '',
         CurrencyId: '',
         MarketCountryId: '',
         MarketAllowsSchoolInvoicing: false,
         MarketPhysicalAddress: '',
         MarketPhoneNumber: '',
         GSTPercentage: 0,
         PartyId: null,
         PartyName: '',
         PartyEmail: '',
         PartyCountryId: null,
         LockCountryForParty: false,
         StoreCreditAvailable: 0,
         HasStoreCreditAvailable: false,
         Addresses: [],
         CreditCards: [],
         SchoolId: null,
         SchoolName: '',
         SchoolAddress: '',
         SchoolCountryId: '',
         SchoolAllowsSchoolInvoicing: false,
         CountryOptions: [],
         DefaultOrderTypeIsPersonalOrder: true,
         DefaultPaymentTypeIsSchoolInvoicing: false,
         HasDigitalProducts: false,
         HasPhysicalProducts: false,
         HasVitalSourceProducts: false,
         PromoteSubscription: false,
         UseGoogleAnalytics: false,
         UseFacebookAnalytics: false,
         RecaptchaSiteKey: '',
         AlgoliaAppId: '',
         AlgoliaApiKey: '',
         AlgoliaIndex: '',

         PasswordMinLength: 8,
         PasswordRestrictionDesc: '',
         PasswordMinUpper: 0,
         PasswordMinLower: 0,
         PasswordMinNumeric: 0,
         PasswordMinPunctuation: 0,
         PasswordPunctuation: '',

      } as CheckoutPageModel,
      purchase: {
         CartId: '',
         PartyId: null,
         EmailAddress: '',
         FirstName: '',
         LastName: '',
         Password: '',
         CountryId: '',
         ClickAndCollect: false,
         GrandTotal: 0,
         UseStoreCredit: false,
         IsPersonalOrder: true,
         IsSchoolInvoicing: false,
         BillingAddress: {} as CheckoutStreetAddressDto,
         BillingAddressId: null,
         ShippingAddress: {} as CheckoutStreetAddressDto,
         ShippingAddressId: '',
         BillingAddressIsSameAsShippingAddress: true,
         CreditCardId: null,
         CardNumber: '',
         NameOnCard: '',
         CardCvc: '',
         ExpiryMonth: 0,
         ExpiryYear: 0,
         SaveCreditCard: false,
         SchoolId: '',
         SchoolName: '',
         SchoolOrderNumber: '',
         SchoolAttention: '',
         Notes: '',
         RecaptchaToken: '',
         AlgoliaAppId: '',
         AlgoliaApiKey: '',
         AlgoliaIndex: '',
      } as StoreCheckoutDto,
      schoolLookup: {
         schoolId: '',
         schoolName: '',
         schoolAddress: '',
      },
   },
   mutations: {
      setModelData(state: CheckoutState, model: CheckoutPageModel) {
         Vue.set(state, 'modelData', model)
         state.purchase.GrandTotal = model.Totals.GrandTotal
         state.modelDataLoaded = true
      },
      setDefaultsFromModelData(state: CheckoutState, model: CheckoutPageModel) {
         state.purchase.CartId = model.Id
         state.purchase.ClickAndCollect = model.AllowClickAndCollect && model.ClickAndCollect
         if (model.PartyId !== null) {
            state.purchase.PartyId = model.PartyId
            state.accountType = 'existing'
         }
         if (!state.purchase.CountryId) {
            if (!!model.PartyCountryId) {
               state.purchase.CountryId = model.PartyCountryId
            } else {
               state.purchase.CountryId = model.ShippingCountryId
            }
         }
         if (!!model.SchoolId) {
            state.purchase.SchoolId = model.SchoolId
         }
         if (!!model.DefaultShippingAddressId) {
            state.purchase.ShippingAddressId = model.DefaultShippingAddressId
         }
         if (!!model.DefaultBillingAddressId) {
            state.purchase.BillingAddressId = model.DefaultBillingAddressId
         }
         if (
            model.MarketAllowsSchoolInvoicing &&
            model.DefaultOrderTypeIsPersonalOrder === false &&
            !!model.SchoolId &&
            model.SchoolCountryId === model.MarketCountryId
         ) {
            state.purchase.IsPersonalOrder = false
         }
         if (!!model.DefaultCreditCardId) {
            state.purchase.CreditCardId = model.DefaultCreditCardId
         }
      },
      setStep(state: CheckoutState, step: Step) {
         if (!state.viewedSteps.includes(state.step)) {
            state.viewedSteps.push(state.step)
         }
         state.step = step
      },
      setOrderType(state: CheckoutState, isPersonalOrder: boolean) {
         state.purchase.IsPersonalOrder = isPersonalOrder
         if (isPersonalOrder) {
            state.purchase.IsSchoolInvoicing = false
         } else if (
            state.modelData.MarketAllowsSchoolInvoicing &&
            state.modelData.SchoolAllowsSchoolInvoicing
         ) {
            state.purchase.IsSchoolInvoicing = true
         }
      },
      setDeliveryMethod(state: CheckoutState, clickAndCollect: boolean) {
         state.purchase.ClickAndCollect = clickAndCollect
      },
      setPaymentMethod(state: CheckoutState, isSchoolInvoicing: boolean) {
         state.purchase.IsSchoolInvoicing = isSchoolInvoicing
         Analytics.addPaymentInfo(
            state.modelData,
            state.purchase.IsSchoolInvoicing ? 'Invoice' : 'Credit Card'
         )
      },
      setSchoolInvoiceData(
         state: CheckoutState,
         payload: { orderNumber: string; attention: string }
      ) {
         state.purchase.SchoolOrderNumber = payload.orderNumber
         state.purchase.SchoolAttention = payload.attention
         Analytics.addPaymentInfo(
            state.modelData,
            state.purchase.IsSchoolInvoicing ? 'Invoice' : 'Credit Card'
         )
      },
      setNewCreditCardData(state: CheckoutState, card: ICreditCard) {
         state.purchase.CreditCardId = null
         state.purchase.CardNumber = card.CardNumber
         state.purchase.NameOnCard = card.NameOnCard
         state.purchase.CardCvc = card.CardCvc
         state.purchase.ExpiryMonth = +card.CardExpiryMonth
         state.purchase.ExpiryYear = +card.CardExpiryYear
         Analytics.addPaymentInfo(
            state.modelData,
            state.purchase.IsSchoolInvoicing ? 'Invoice' : 'Credit Card'
         )
      },
      setExistingCreditCardData(
         state: CheckoutState,
         card: { CreditCardId: string; CardNumber: string }
      ) {
         state.purchase.CardNumber = card.CardNumber
         state.purchase.CreditCardId = card.CreditCardId
         Analytics.addPaymentInfo(
            state.modelData,
            state.purchase.IsSchoolInvoicing ? 'Invoice' : 'Credit Card'
         )
      },
      setShippingAddressId(state: CheckoutState, addressId: string) {
         state.purchase.ShippingAddressId = addressId
      },
      setBillingAddressId(state: CheckoutState, addressId: string) {
         state.purchase.BillingAddressId = addressId
      },
      setBillingAddressIsSameAsShippingAddress(state: CheckoutState, isSame: boolean) {
         state.purchase.BillingAddressIsSameAsShippingAddress = isSame
         if (isSame) {
            state.purchase.BillingAddressId = state.purchase.ShippingAddressId
         }
      },
      setAddress(
         state: CheckoutState,
         payload: { type: 'Shipping' | 'Billing'; address: CheckoutStreetAddressDto }
      ) {
         if (payload.type === 'Shipping') {
            Vue.$assign(state.purchase.ShippingAddress, payload.address)
            state.purchase.ShippingAddressId = null
            if (
               !state.purchase.BillingAddress.AddressLine1 &&
               !state.purchase.BillingAddress.ContactPhoneNumber
            ) {
               state.purchase.BillingAddress.ContactPhoneNumber =
                  state.purchase.ShippingAddress.ContactPhoneNumber
            }
            Analytics.addShippingInfo(state.modelData)
         } else if (payload.type === 'Billing') {
            Vue.$assign(state.purchase.BillingAddress, payload.address)
            state.purchase.BillingAddressId = null
         }
      },
      setContactPhoneNumber( state: CheckoutState, contactPhoneNumber: string ) {
         const newShippingAddress = Object.assign({}, state.purchase.ShippingAddress)
         newShippingAddress.ContactPhoneNumber = contactPhoneNumber
         Vue.$assign(state.purchase.ShippingAddress, newShippingAddress)
      },
      setSchoolLookupResult(state: CheckoutState, school: SchoolSearchResultModel) {
         if (school) {
            state.schoolLookup.schoolId = school.Id
            state.schoolLookup.schoolName = school.Name
            state.schoolLookup.schoolAddress = school.AddressSummary
            state.purchase.SchoolId = school.Id
         }
      },
      setUseStoreCredit(state: CheckoutState, useCredit: boolean) {
         state.purchase.UseStoreCredit = useCredit
      },
      setRecaptchaToken(state: CheckoutState, token: string) {
         state.purchase.RecaptchaToken = token
      },
      setRecaptchaRefreshRequired(state: CheckoutState, required: boolean) {
         state.recaptchaRefreshRequired = required
      },
      setEmail(state: CheckoutState, email: string) {
         state.purchase.EmailAddress = email
      },
      setFirstname(state: CheckoutState, firstname: string) {
         state.purchase.FirstName = firstname
      },
      setLastname(state: CheckoutState, lastname: string) {
         state.purchase.LastName = lastname
      },
      setPassword(state: CheckoutState, password: string) {
         state.purchase.Password = password
      },
      setAccountType(state: CheckoutState, accountType: 'existing' | 'new') {
         state.accountType = accountType
      },
      setSaveCard(state: CheckoutState, save: boolean) {
         state.purchase.SaveCreditCard = save
      },
      setProcessingCheckout(state: CheckoutState, processing: boolean) {
         state.processingCheckout = processing
      },
      setVerificationErrors(state: CheckoutState, dto: CheckoutVerificationResult) {
         if (!dto.Success) {
            state.verificationErrorMessage = 'Please correct the following issues and try again'
            state.verificationErrors = dto.ErrorMessages
         }
      },
      clearVerificationErrors(state: CheckoutState) {
         state.verificationErrorMessage = ''
         state.verificationErrors = []
      },
      setCheckoutErrors(state: CheckoutState, dto: CheckoutResponse) {
         if (!dto.Success) {
            if (dto.CheckoutResultLabel === 'CreditCardPaymentFailure') {
               state.verificationErrorMessage = 'Payment Error'
               state.verificationErrors = [
                  'The payment was not successful.  Please try again.',
                  'The message from the payment provider is ' + dto.CheckoutResultMessage,
               ]
               state.recaptchaRefreshRequired = true
            } else if (dto.ErrorMessages && dto.ErrorMessages.length > 0) {
               state.verificationErrorMessage = dto.CheckoutResultMessage
               state.verificationErrors = dto.ErrorMessages
            } else {
               state.verificationErrorMessage = 'Something went wrong while processing your order'
               state.verificationErrors = [
                  'Please correct the following issues and try again.',
                  dto.CheckoutResultMessage,
               ]
            }
         }
      },
      setShowErrors(state: CheckoutState, show: boolean) {
         state.showErrors = show
      },
   },
   actions: {
      async loadModelData({ state, commit }) {
         if (!state.modelDataLoaded) {
            const model = await Api.getModelData()
            commit('setModelData', model)
            commit('setDefaultsFromModelData', model)
            Analytics.beginCheckout(state.modelData)
         }
      },
      async refreshModelData({ state, commit }) {
         const model = await Api.getModelData(state.purchase.UseStoreCredit)
         commit('setModelData', model)
         return model
      },
      async applyPromocode({ dispatch }, code: string) {
         const result = await Api.addPromocode(code)
         if (result.HasError) {
            return result
         }
         await dispatch('refreshModelData')
         return result
      },
      async removePromocode({ dispatch }, code: string) {
         const result = await Api.removePromocode(code)
         if (result.HasError) {
            return result
         }
         await dispatch('refreshModelData')
         return result
      },
      async setShippingAddressId({ state, dispatch, commit }, addressId) {
         if (state.purchase.ShippingAddressId === addressId) {
            return
         }
         commit('setShippingAddressId', addressId)
         const address = state.modelData.Addresses.find(a => a.Id === addressId)
         if (address) {
            await dispatch('setShippingCountryId', address.CountryId)
         }
      },
      async setShippingCountryId({ state, dispatch }, countryId: string) {
         await Api.setShippingCountryId(countryId)
         await dispatch('refreshModelData')
      },
      async setBillingAddressId({ commit }, addressId) {
         commit('setBillingAddressId', addressId)
      },
      async setOrderType({ commit, dispatch }, isPersonalOrder: boolean) {
         commit('setOrderType', isPersonalOrder)
         await dispatch('setDeliveryMethod', false)
      },
      async setPaymentMethod({ commit, state }, isSchoolInvoicing: boolean) {
         commit('setPaymentMethod', isSchoolInvoicing)
      },
      async setDeliveryMethod({ commit, dispatch }, clickAndCollect: boolean) {
         commit('setDeliveryMethod', clickAndCollect)
         await Api.setDeliveryMethod(clickAndCollect)
         await dispatch('refreshModelData')
      },
      async setUseStoreCredit({ commit, dispatch }, useStoreCredit: boolean) {
         commit('setUseStoreCredit', useStoreCredit)
         await dispatch('refreshModelData')
      },
      async completeCheckout({ state, commit, getters }) {
         if (getters.validationMessages.length > 0) {
            commit('setShowErrors', true)
            return
         }
         commit('setProcessingCheckout', true)
         commit('clearVerificationErrors')
         const verificationResult = await Api.verifyCheckout(state.purchase)
         if (!verificationResult.Success) {
            commit('setVerificationErrors', verificationResult)
            commit('setProcessingCheckout', false)
            return
         }
         try {
            const checkoutResult: CheckoutResponse = await Api.completeCheckout(state.purchase)
            if (!checkoutResult.Success) {
               commit('setCheckoutErrors', checkoutResult)
               commit('setProcessingCheckout', false)
               return
            }

            const data = Analytics.forCheckout(checkoutResult.OrderNumber, state.modelData)
            await Api.logAnalytics(data)
            Api.redirectToOrder(checkoutResult, getters.hasLoggedInUser)
         } catch (err) {
            commit('setCheckoutErrors', {
               Success: false,
               CheckoutResultMessage: 'Oops, something went wrong.  Please try again.',
            } as CheckoutResponse)
            commit('setProcessingCheckout', false)
         }
      },
   },
   getters: {
      currencyCode(state: CheckoutState): string {
         return state.modelData.CurrencyCode
      },
      hasLoggedInUser(state: CheckoutState): boolean {
         return state.modelData.PartyId !== null
      },
      hasSchool(state: CheckoutState): boolean {
         return state.purchase.SchoolId != null
      },
      username(state: CheckoutState, getters): string {
         return getters.hasLoggedInUser ? state.modelData.PartyEmail : state.purchase.EmailAddress
      },
      partyName(state: CheckoutState, getters): string {
         return getters.hasLoggedInUser
            ? state.modelData.PartyName
            : `${state.purchase.FirstName} ${state.purchase.LastName}`
      },
      schoolName(state: CheckoutState): string {
         const hasAlreadyAssociatedSchool = !!state.modelData.SchoolId
         const hasLookedUpSchool = !!state.schoolLookup.schoolId
         const hasSchool = hasLookedUpSchool || hasAlreadyAssociatedSchool

         if (!hasSchool) {
            return ''
         }

         return hasLookedUpSchool ? state.schoolLookup.schoolName : state.modelData.SchoolName
      },
      schoolAllowsInvoicing(state: CheckoutState): boolean {
         const hasLookedUpSchool = !!state.schoolLookup.schoolId
         if (hasLookedUpSchool) {
            return state.modelData.MarketAllowsSchoolInvoicing
         }
         return (
            state.modelData.MarketAllowsSchoolInvoicing &&
            state.modelData.SchoolAllowsSchoolInvoicing
         )
      },
      requiresDelivery(state: CheckoutState): boolean {
         return state.modelData.HasPhysicalProducts
      },
      allowClickAndCollect(state: CheckoutState): boolean {
         return state.modelData.AllowClickAndCollect
      },
      clickAndCollect(state: CheckoutState): boolean {
         return state.purchase.ClickAndCollect
      },
      requiresShippingAddress(state: CheckoutState): boolean {
         return state.modelData.HasPhysicalProducts && state.purchase.IsPersonalOrder
      },
      requiresBillingAddress(state: CheckoutState): boolean {
         return !state.modelData.HasPhysicalProducts && state.purchase.IsPersonalOrder
      },
      requiresPayment(state: CheckoutState): boolean {
         return state.modelData.Totals.GrandTotal > 0
      },
      useStoreCredit(state: CheckoutState): boolean {
         return state.purchase.UseStoreCredit
      },
      canCompleteInformationStep(state: CheckoutState): boolean {
         return informationValidationMessages(state).length === 0
      },
      canCompleteOrderTypeStep(state: CheckoutState): boolean {
         return orderTypeValidationMessages(state).length === 0
      },
      canCompleteShippingStep(state: CheckoutState): boolean {
         return shippingValidationMessages(state).length === 0
      },
      canCompletePaymentStep(state: CheckoutState): boolean {
         return paymentValidationMessages(state).length === 0
      },
      validationMessages(state: CheckoutState): ValidationMessage[] {
         return allValidationMessages(state)
      },
   },
})
